import { IconChevronRight, IconReceipt2, IconUser } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button, Card, Text } from '@fragments';
import Parser from '@helpers/parser';
import { openProfile } from '@helpers/profile';
import { getIntlLocale } from '@i18n';

import { RowsSkeleton } from '../Skeletons';

import './styles.scss';

function createdLabel(iso) {
    if (!iso) return '';
    const date = new Date(String(iso).replace(' ', 'T'));
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(getIntlLocale(), { day: 'numeric', month: 'long', year: 'numeric' });
}

// Same destination the banking subscriptions table uses on row click.
function openSubscriber(subscription) {
    const userId = subscription?.user?.id;
    if (userId) window.navigate(`/user/${userId}`);
}

/**
 * RecentSubscriptionsCard — the latest 10 subscriptions of the active owner
 * community, in the "Recent Transactions" grammar: leading rounded-square chip
 * (subscriber avatar), a min-width:0 column [name + plan · status], and a
 * right-aligned column [amount over date]. Only the list scrolls — the header
 * and footer stay put. Each row opens the subscriber's page. `split` hands the
 * header / body / footer to the owner bento as separate blocks (see Card).
 */
export default function RecentSubscriptionsCard({ subscriptions, loading, split = false }) {
    const { t } = useTranslation('dashboard');

    const items = subscriptions?.items || [];

    const STATUS_LABEL = {
        active: t('common:payment.subscriptionStatus.active'),
        canceled: t('common:payment.subscriptionStatus.canceled'),
        expired: t('common:payment.subscriptionStatus.expired'),
    };

    return (
        <Card
            flat={!split}
            split={split}
            noBodyPadding
            headerDivider
            className="rec-subs-card"
            title={t('home.recentSubscriptions.title')}
            subtitle={t('home.recentSubscriptions.subtitle')}
            icon={<IconReceipt2 />}
            footer={
                <Button
                    color="inverse"
                    variant="text"
                    size="small"
                    fullWidth
                    endIcon={<IconChevronRight />}
                    onClick={() => window.navigate('/banking/subscriptions')}
                >
                    {t('home.recentSubscriptions.viewAll')}
                </Button>
            }
        >
            {loading ? (
                <RowsSkeleton rows={6} />
            ) : items.length === 0 ? (
                <div className="rec-subs__empty">
                    <Text.span contrast={6}>{t('home.recentSubscriptions.empty')}</Text.span>
                    <Text.caption contrast={5}>{t('home.recentSubscriptions.emptyHint')}</Text.caption>
                </div>
            ) : (
                <ul className="rec-subs__list">
                    {items.map((s, i) => {
                        const name = s.user?.username || t('home.recentSubscriptions.subscriberFallback');
                        const plan = s.plan?.label || t('home.recentSubscriptions.planFallback');
                        const status = STATUS_LABEL[s.status] || s.status || '';
                        const clickable = !!s.user?.id;
                        return (
                            <li
                                key={s.id || i}
                                className={`rec-subs__row${clickable ? ' rec-subs__row--clickable' : ''}`}
                                role={clickable ? 'button' : undefined}
                                tabIndex={clickable ? 0 : undefined}
                                onClick={clickable ? () => openSubscriber(s) : undefined}
                                onKeyDown={clickable ? (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openSubscriber(s);
                                    }
                                } : undefined}
                            >
                                {/* The row opens the billing view of the member; the
                                    avatar chip alone opens their public profile. */}
                                <span
                                    className={`rec-subs__chip${clickable ? ' rec-subs__chip--link' : ''}`}
                                    aria-hidden={clickable ? undefined : 'true'}
                                    title={clickable ? name : undefined}
                                    onClick={clickable ? (e) => { e.stopPropagation(); openProfile(s.user); } : undefined}
                                >
                                    {s.user?.avatar ? <img src={s.user.avatar} alt="" /> : <IconUser />}
                                </span>

                                <div className="rec-subs__info">
                                    <Text.label contrast={9} maxLines={1}>{name}</Text.label>
                                    <Text.caption contrast={6} maxLines={1}>
                                        {status ? `${plan} · ${status}` : plan}
                                    </Text.caption>
                                </div>

                                <div className="rec-subs__trailing">
                                    <Text.label contrast={9} maxLines={1}>
                                        {Parser.price.format(s.price?.amount ?? 0, s.price?.currency || 'BRL')}
                                    </Text.label>
                                    <Text.caption contrast={5}>{createdLabel(s.timestamps?.created)}</Text.caption>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </Card>
    );
}
